import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import Text from '~/common/components/Texts/Text';
import Grid from '~/common/mui/Grids/Grid';
import { formatCurrency } from '~/common/util/format';
import { INCOME_TYPES } from '~/pages/IncomeCalculator/constants';

type IncomeCategoryProps = Readonly<{
  employmentType?: string;
  isCurrent: boolean;
  isPrimary: boolean;
}>;

const IncomeCategory = ({
  employmentType,
  isPrimary,
  isCurrent,
}: IncomeCategoryProps) => {
  let categoryText: string;

  if (!isCurrent) {
    categoryText = 'Employment - Previous';
  } else if (isPrimary) {
    categoryText = `${employmentType} - Primary`;
  } else if (employmentType) {
    categoryText = employmentType;
  } else {
    return null;
  }

  return (
    <Text data-testid="wage-earner-text">
      <b>Income Source:</b> &nbsp;
      {categoryText}
    </Text>
  );
};

type IncomeSourceHeaderProps = Readonly<{
  monthlyIncome: number;
  totalIncome?: number;
  dti?: number;
  fullName: string;
  incomeCategory?: string;
  incomeTypes?: string[];
  isPrimary: boolean;
  startDate: string;
  endDate?: string;
  isCurrent?: boolean;
  isWriteAccessDisabled?: boolean;
  showForm1003ButtonInIncomeHeader?: boolean;
}>;

const IncomeSourceHeader = ({
  monthlyIncome,
  fullName,
  incomeCategory,
  incomeTypes,
  isPrimary,
  startDate,
  endDate,
  isCurrent,
  isWriteAccessDisabled,
  ...rest
}: IncomeSourceHeaderProps) => {
  const displayEndDate = endDate && endDate !== '12/31/1969' ? endDate : 'N/A';
  const isVaBenefits = incomeTypes?.includes(INCOME_TYPES.VA_BENEFITS.name);

  return (
    <Grid
      gridTemplateColumns="1fr 1fr"
      fontSize="sm"
      fontWeight={500}
      sx={{
        opacity: isWriteAccessDisabled ? 0.6 : 1,
      }}
      {...rest}
    >
      <Box>
        <Box>
          <Text sx={{ display: 'inline' }}>
            <b>Current Monthly Income in System:</b> &nbsp;
            {formatCurrency(monthlyIncome)}
          </Text>
        </Box>
        <IncomeCategory
          employmentType={incomeCategory}
          isPrimary={isPrimary}
          isCurrent={isCurrent}
        />
      </Box>
      <Flex direction="row" alignItems="center" justifyContent="center">
        <Box textAlign="right">
          <Text data-testid="fullname-text">{fullName}</Text>
          {!isVaBenefits &&
            isCurrent &&
            startDate !== null &&
            startDate !== '12/31/1969' && (
              <Text data-testid="start-date-text">
                <b>Start Date:</b>&nbsp; {startDate}
              </Text>
            )}
          {!isVaBenefits && !isCurrent && (
            <Text data-testid="start-date-text">
              <b>Dates Employed:</b>&nbsp; {startDate} - {displayEndDate}
            </Text>
          )}
        </Box>
      </Flex>
    </Grid>
  );
};

export default IncomeSourceHeader;
